import type { ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

/**
 * How loud the icon and the eyebrow are. One prop for both, because they are
 * the same gesture: whatever marks this block is either a quiet label, a
 * full-strength one, or the brand's.
 *
 * `brand` is `--brand-text`, the text-grade mint, not `--brand`. The bright
 * mint is 1.4:1 on the page and simply not there; this is the same colour to
 * the eye at the step that can be read on this ground.
 *
 * Not called a tone. `Tone` in this repo means danger / warning / success /
 * neutral — what a message means — and a second sense of the word is exactly
 * the drift `lib/tone.ts` exists to prevent.
 */
const ACCENT = {
  muted: 'text-ink-muted',
  ink: 'text-ink',
  brand: 'text-brand-text',
  info: 'text-info-text',
} as const;

/**
 * The fill behind the glyph when it is set as a tile, per accent.
 *
 * Each one is the wash that belongs to the accent already chosen — there is no
 * separate colour decision here, which is why this is a lookup rather than a
 * prop. The foreground is whatever `ACCENT` gave the glyph, so the pairing is
 * the wash-and-text one the status roles already use (`--warn-wash` with
 * `--warn-text`, and so on) rather than a new guess.
 *
 * `muted` and `ink` share the neutral tint because neither is a hue: a quiet
 * mark and a full-strength one differ in how loud the glyph is, not in what
 * colour the tile behind it should be.
 */
const TILE = {
  muted: 'bg-raised',
  ink: 'bg-raised',
  brand: 'bg-brand-wash',
  info: 'bg-info-wash',
} as const;

export type IconCardProps = {
  /**
   * The glyph on the first line — a react-icons or lucide element, usually.
   *
   * It pairs with whichever text part comes first, and the parts under that one
   * are indented to clear it. Without an icon nothing is indented and the text
   * runs full width.
   */
  icon?: ReactNode;
  /**
   * Sets the glyph as a tile above the text rather than inline beside it — a
   * 44px round wash with the glyph centred in it, and the copy starting under
   * the whole thing.
   *
   * This is not the layout prop the note above rules out. That one would be a
   * second way to say something the parts already say, and the two could
   * disagree; this says how the *glyph* is drawn, which nothing else states.
   * It also removes a question rather than adding one — with the icon above the
   * stack there is no first line for it to pair with, so the line-high icon box and the
   * `ml-7` indent both stop applying instead of needing a rule.
   *
   * Nothing without an `icon`, obviously. The larger gesture wants more room
   * around it, so it reads as a card's mark rather than a bullet — `plain` will
   * work but `card` and `filled` are what it is for.
   */
  iconTile?: boolean;
  /** The mono label — "BOOKING", "WHO IT IS FOR". */
  eyebrow?: string;
  /** How loud the icon and eyebrow are set. */
  accent?: keyof typeof ACCENT;
  /**
   * The line that matters — a phone number, a price, a name. Set largest and
   * boldest of the three.
   */
  title?: string;
  /**
   * How large the title is set. `lg` is the headline figure — a mission line,
   * a price. `md` is for a row of them set side by side, where each value is
   * one of several. `sm` is for a row of four, where a long value has a
   * quarter of the page to fit in, and `xs` for one too long even for that (an
   * email address on a long domain), set at body size.
   */
  titleSize?: 'lg' | 'md' | 'sm' | 'xs';
  /**
   * The sentence.
   *
   * A string, not a node: this is one line of prose in a known typeface at a
   * known size, and accepting markup would make that a suggestion. A block that
   * needs a link in its sentence is a different component.
   */
  description?: string;
  /**
   * Sets the description as an aside rather than as the content — smaller and
   * in the muted ink, for the line under a phone number that says when to use
   * it.
   *
   * A modifier on the one description rather than a second text prop beside
   * it. Two props that both take a sentence and differ only in how it is drawn
   * are two names for one slot, and the question of which to use has no answer
   * at the call site. There is one sentence; this says how loud it is.
   */
  quiet?: boolean;
  /**
   * A hairline across the top, for a column of these read as a list — the rule
   * belongs to the block under it rather than to the one above, so the last
   * block in a column does not trail an edge with nothing beneath it.
   *
   * `plain` only. The other two variants are already bounded on all four sides,
   * so a fifth edge across the top would land on the border that is there and
   * its padding would fight `p-card`.
   */
  ruled?: boolean;
  /**
   * The chrome. `plain` sits straight on the page; `card` is the bordered white
   * card; `filled` is the same shape in the raised tint, no border.
   */
  variant?: 'plain' | 'card' | 'filled';
  /**
   * Lines the block's rows up with the blocks beside it: the label, the title
   * and the sentence each sit on a row shared across the whole grid row, so a
   * title that wraps in one block moves every block's sentence down with it
   * rather than only its own.
   *
   * Needs a CSS grid parent, and works by subgrid — the block spans one of the
   * parent's rows per part it carries. Every block in a row should carry the
   * same parts, or the shorter ones leave a row empty.
   */
  aligned?: boolean;
  className?: string;
};

/**
 * A line or a block of text with an optional glyph beside it.
 *
 * The arrangement is not a prop — it falls out of which parts are passed. The
 * icon sits on the first line there is, and everything after that line stacks
 * under it:
 *
 *   icon + description            one line, the glyph beside the sentence
 *   icon + title + description    glyph and title on the first line, sentence
 *                                 on the second
 *   title + description           a line each, nothing indented
 *   eyebrow + anything            the eyebrow is first, so the icon pairs with
 *                                 that and the rest falls below
 *
 * Deriving it is the point. A layout prop beside these would be a second way to
 * say the same thing, and the two could disagree — `align="center"` on a block
 * with three lines of text is a state that should not be expressible.
 *
 * One component rather than five because the five are the same object with
 * different parts filled in: a bullet is an icon and a sentence, a contact
 * detail is a label and a number and an aside, a section heading is a label and
 * a sentence.
 */
export default function IconCard({
  icon,
  iconTile,
  eyebrow,
  accent = 'muted',
  title,
  titleSize = 'lg',
  description,
  quiet,
  ruled,
  variant = 'plain',
  aligned,
  className,
}: IconCardProps) {
  const accented = ACCENT[accent];

  // In the order they are drawn. The first is what the icon pairs with; the
  // rest stack under it, which is the whole of the layout logic.
  //
  // Each row carries its type class, so the icon can take the lead row's: its
  // box is `h-lh` — one line of that type — which centres the glyph on the
  // first line at any size, on any screen. The type roles (`text-body`,
  // `text-heading-lg`…) step down on a phone from the tokens, so nothing here has a
  // breakpoint class.
  const rows: { key: string; type: string; gap: string; content: ReactNode }[] = [];

  if (eyebrow) {
    const type = 'font-mono text-2xs uppercase';
    rows.push({
      key: 'eyebrow',
      type,
      gap: 'mt-1',
      content: <span className={cn('block', type, accented)}>{eyebrow}</span>,
    });
  }

  if (title) {
    const type = {
      xs: 'text-body',
      sm: 'text-body-lg',
      md: 'text-heading-md',
      lg: 'text-heading-lg',
    }[titleSize];
    rows.push({
      key: 'title',
      type,
      gap: 'mt-1',
      // `wrap-anywhere`: an email or a URL is one unbroken word, and without
      // a place to break it runs out of its column into the next one.
      content: <p className={cn(type, 'font-bold text-ink wrap-anywhere')}>{title}</p>,
    });
  }

  if (description) {
    const type = quiet ? 'text-body-sm' : 'text-body';
    rows.push({
      key: 'description',
      type,
      // An aside gets more air above it than a second line of the same thought.
      gap: quiet ? 'mt-2' : 'mt-1',
      content: <p className={cn(type, quiet ? 'text-ink-muted' : 'text-ink')}>{description}</p>,
    });
  }

  const [lead, ...rest] = rows;
  if (!lead) return null;

  // Indented to land under the lead text rather than under the glyph: 16px of
  // icon plus the 12px gap. Without an icon there is nothing to clear, and a
  // tiled icon is above the stack rather than beside it.
  const indent = cn(icon && !iconTile && 'ml-7');

  if (aligned) {
    // One parent row per part, each part a direct child so it lands on its
    // own shared row. The tile, when there is one, takes a row of its own too.
    const span = rows.length + (icon && iconTile ? 1 : 0);
    return (
      <div
        className={cn(
          'grid grid-rows-subgrid gap-y-0',
          ruled && variant === 'plain' && 'border-t border-hairline pt-5',
          variant === 'card' && 'rounded-card border border-hairline bg-surface p-card',
          variant === 'filled' && 'rounded-card bg-raised p-card',
          className,
        )}
        style={{ gridRow: `span ${span}` }}
      >
        {icon && iconTile && (
          <span
            className={cn(
              'mb-4 flex size-11 items-center justify-center rounded-full [&_svg]:size-5',
              TILE[accent],
              accented,
            )}
            aria-hidden
          >
            {icon}
          </span>
        )}
        <div className="flex items-start gap-3">
          {icon && !iconTile && (
            <span
              className={cn('flex h-lh shrink-0 items-center [&_svg]:size-4', lead.type, accented)}
              aria-hidden
            >
              {icon}
            </span>
          )}
          <div className="min-w-0">{lead.content}</div>
        </div>
        {rest.map((row) => (
          <div key={row.key} className={cn(indent, row.gap)}>
            {row.content}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        ruled && variant === 'plain' && 'border-t border-hairline pt-5',
        variant === 'card' && 'rounded-card border border-hairline bg-surface p-card',
        variant === 'filled' && 'rounded-card bg-raised p-card',
        className,
      )}
    >
      {icon && iconTile && (
        // Above the stack rather than in it, so it is the card's mark rather
        // than a glyph on a line. 44px is `--target-min`, which is not a
        // coincidence worth relying on but is the right size for a mark that
        // has to hold its own against a 26px title under it.
        <span
          className={cn(
            'mb-4 flex size-11 items-center justify-center rounded-full [&_svg]:size-5',
            TILE[accent],
            accented,
          )}
          aria-hidden
        >
          {icon}
        </span>
      )}

      <div className="flex items-start gap-3">
        {icon && !iconTile && (
          // The size is set here rather than left to the caller: an icon passed
          // as `<LuCalendar />` arrives at whatever that set's default is, and a
          // column of these would end up with glyphs at three sizes.
          <span
            className={cn('flex h-lh shrink-0 items-center [&_svg]:size-4', lead.type, accented)}
            aria-hidden
          >
            {icon}
          </span>
        )}

        {/* `min-w-0` so a long unbroken string — a URL, a phone number — wraps
            instead of pushing the row wider than its container. */}
        <div className="min-w-0">{lead.content}</div>
      </div>

      {rest.length > 0 && (
        <div className={indent}>
          {rest.map((row) => (
            <div key={row.key} className={row.gap}>
              {row.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
